import { useEffect, useId, useRef } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import clsx from "clsx";
import { Loader2, X } from "lucide-react";
import type { VideoStatus } from "../lib/types";
import { hueFor, initials } from "../lib/format";
import { useI18n } from "../i18n";

/* -------------------------------------------------------------- primitives */

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "subtle";
type ButtonSize = "sm" | "md" | "lg";

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition duration-200 " +
  "disabled:cursor-not-allowed disabled:opacity-50 active:translate-y-px select-none whitespace-nowrap";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-linear-to-b from-brand-400 to-brand-600 text-white shadow-lift hover:from-brand-300 hover:to-brand-500",
  secondary:
    "bg-[var(--color-surface-2)] text-[var(--color-ink)] border border-[var(--color-hairline)] hover:bg-[var(--color-surface)]",
  ghost: "text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
  danger: "bg-rose-600 text-white hover:bg-rose-500",
  subtle: "bg-white/10 text-white backdrop-blur hover:bg-white/20",
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-11 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  block?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  block = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={clsx(
        BUTTON_BASE,
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        block && "w-full",
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

export function IconButton({
  label,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx(
        "inline-flex size-10 items-center justify-center rounded-xl text-[var(--color-ink-muted)]",
        "transition hover:bg-[var(--color-surface-2)] hover:text-[var(--color-ink)]",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Spinner({ className }: { className?: string }) {
  const { t } = useI18n();
  return (
    <span role="status" aria-label={t("common.loading")}>
      <Loader2 className={clsx("size-5 animate-spin text-brand-400", className)} aria-hidden />
    </span>
  );
}

export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={clsx("panel shadow-lift", className)}>{children}</div>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx("skeleton rounded-xl", className)} aria-hidden />;
}

/* ------------------------------------------------------------------- forms */

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string) => ReactNode;
}

export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-[var(--color-ink)]">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="text-xs text-rose-400" dir="auto">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-[var(--color-ink-faint)]" dir="auto">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const CONTROL =
  "w-full rounded-xl border border-[var(--color-hairline)] bg-[var(--color-surface-2)] px-3.5 py-2.5 " +
  "text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] transition " +
  "hover:border-brand-500/40 focus:border-brand-500 focus:outline-none";

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx(CONTROL, className)} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={clsx(CONTROL, "min-h-24 resize-y leading-relaxed", className)} {...rest} />;
}

export function Select({
  className,
  children,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={clsx(CONTROL, "cursor-pointer appearance-none bg-[var(--color-surface-2)]", className)} {...rest}>
      {children}
    </select>
  );
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="group flex items-center gap-3 text-sm text-[var(--color-ink)]"
    >
      <span
        className={clsx(
          "relative h-6 w-11 shrink-0 rounded-full transition",
          checked ? "bg-brand-500" : "bg-[var(--color-surface-2)] border border-[var(--color-hairline)]",
        )}
      >
        <span
          className={clsx(
            "absolute top-0.5 size-5 rounded-full bg-white shadow transition-all",
            checked ? "start-5.5" : "start-0.5",
          )}
        />
      </span>
      <span dir="auto">{label}</span>
    </button>
  );
}

/* ---------------------------------------------------------------- displays */

const STATUS_TONES: Record<VideoStatus, string> = {
  UPLOADING: "bg-slate-500/15 text-slate-300 border-slate-400/25",
  VALIDATING: "bg-sky-500/15 text-sky-300 border-sky-400/25",
  MODERATING: "bg-indigo-500/15 text-indigo-300 border-indigo-400/25",
  READY: "bg-teal-500/15 text-teal-300 border-teal-400/25",
  PUBLISHED: "bg-emerald-500/15 text-emerald-300 border-emerald-400/25",
  FLAGGED: "bg-amber-500/15 text-amber-300 border-amber-400/25",
  REJECTED: "bg-rose-500/15 text-rose-300 border-rose-400/25",
  DELETED: "bg-zinc-500/15 text-zinc-400 border-zinc-400/25",
};

export function StatusBadge({ status }: { status: VideoStatus }) {
  const { t } = useI18n();
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        STATUS_TONES[status],
      )}
    >
      {t(("status." + status) as "status.PUBLISHED")}
    </span>
  );
}

export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full border border-[var(--color-hairline)]",
        "bg-[var(--color-surface-2)] px-2.5 py-0.5 text-xs text-[var(--color-ink-muted)]",
        className,
      )}
      dir="auto"
    >
      {children}
    </span>
  );
}

export function Avatar({
  username,
  url,
  size = 40,
  className,
}: {
  username: string;
  url?: string | null;
  size?: number;
  className?: string;
}) {
  const hue = hueFor(username);
  return url ? (
    <img
      src={url}
      alt={username}
      width={size}
      height={size}
      loading="lazy"
      className={clsx("shrink-0 rounded-full object-cover ring-1 ring-[var(--color-hairline)]", className)}
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      aria-hidden
      className={clsx(
        "grid shrink-0 place-items-center rounded-full font-semibold text-white ring-1 ring-white/15",
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        background: `linear-gradient(140deg, hsl(${hue} 70% 52%), hsl(${(hue + 48) % 360} 72% 42%))`,
      }}
    >
      {initials(username)}
    </span>
  );
}

export function ProgressBar({ value, className }: { value: number; className?: string }) {
  const percent = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div
      className={clsx("h-2 w-full overflow-hidden rounded-full bg-[var(--color-surface-2)]", className)}
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-linear-to-r from-brand-400 to-accent-400 transition-all duration-300"
        style={{ width: percent + "%" }}
      />
    </div>
  );
}

export function Stat({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: string;
}) {
  return (
    <Panel className="p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium tracking-wide text-[var(--color-ink-faint)] uppercase">
          {label}
        </p>
        {icon ? <span className={clsx("text-[var(--color-ink-faint)]", tone)}>{icon}</span> : null}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums">{value}</p>
    </Panel>
  );
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      {icon ? <div className="text-[var(--color-ink-faint)]">{icon}</div> : null}
      <h2 className="text-lg font-semibold" dir="auto">
        {title}
      </h2>
      {hint ? (
        <p className="max-w-sm text-sm text-[var(--color-ink-muted)]" dir="auto">
          {hint}
        </p>
      ) : null}
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------- modal */

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-90 grid place-items-center p-4">
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/60 backdrop-blur-sm"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="animate-rise panel relative w-full max-w-lg p-5 shadow-lift"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="text-lg font-semibold" dir="auto">
            {title}
          </h2>
          <IconButton label={t("common.close")} onClick={onClose} className="-me-2 -mt-1 size-9">
            <X className="size-4" aria-hidden />
          </IconButton>
        </div>
        {children}
        {footer ? <div className="mt-5 flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>
  );
}
